import React from "react";
import { ExternalLink } from "react-feather";
import "./agent-steps-tracker.scss";

export type AgentStepStatus = "completed" | "action-required";

/**
 * Colour overrides. Every value maps to a CSS custom property and can be set
 * globally (on the component) or per-step. Any CSS colour string works
 * (hex, rgb, hsl, var(), etc).
 */
export interface AgentStepsTrackerColors {
  /** Root card background. */
  background?: string;
  /** Root card border colour. */
  borderColor?: string;
  /** Title text colour. */
  titleColor?: string;
  /** Connector line colour between markers. Defaults to the marker icon colour. */
  lineColor?: string;
  /** Completed-marker background. */
  markerBackground?: string;
  /** Completed-marker border (ring) colour. */
  markerBorderColor?: string;
  /** Completed-marker icon colour. */
  iconColor?: string;
  /** Step label text colour. */
  labelColor?: string;
  /** Action card background. */
  cardBackground?: string;
  /** Action card border colour. */
  cardBorderColor?: string;
  /** Action card title text colour. */
  cardTitleColor?: string;
  /** Action marker background. */
  actionMarkerBackground?: string;
  /** Action marker border (ring) colour. */
  actionMarkerBorderColor?: string;
  /** Action marker icon colour. */
  actionIconColor?: string;
  /** Tag text colour. */
  tagColor?: string;
  /** Tag background colour. */
  tagBackground?: string;
  /** Checklist heading colour. */
  actionTitleColor?: string;
  /** Checklist item text colour. */
  actionItemColor?: string;
  /** External-link icon colour. */
  linkColor?: string;
  /** External-link icon hover colour. */
  linkHoverColor?: string;
}

/** Size / layout overrides. Any CSS length string works (px, rem, %, etc). */
export interface AgentStepsTrackerSizes {
  /** Font family for the whole component. */
  fontFamily?: string;
  /** Root card corner radius. */
  borderRadius?: string;
  /** Root card padding (any CSS padding shorthand). */
  padding?: string;
  /** Gap between the title and the first step. */
  titleGap?: string;
  /** Vertical gap between steps. */
  stepGap?: string;
  /** Diameter of completed-step markers. */
  markerSize?: string;
  /** Gap between a marker and its label. */
  markerGap?: string;
  /** Diameter of the action-step marker. */
  actionMarkerSize?: string;
  /** Thickness of the connector line. */
  lineWidth?: string;
  /** How much shorter the connector line is directly above/below an action card. */
  actionLineTrim?: string;
  /** Action card corner radius. */
  cardRadius?: string;
}

const COLOR_VAR_MAP: Record<keyof AgentStepsTrackerColors, string> = {
  background: "--ast-bg",
  borderColor: "--ast-border-color",
  titleColor: "--ast-title-color",
  lineColor: "--ast-line-color",
  markerBackground: "--ast-marker-bg",
  markerBorderColor: "--ast-marker-border-color",
  iconColor: "--ast-marker-icon-color",
  labelColor: "--ast-label-color",
  cardBackground: "--ast-card-bg",
  cardBorderColor: "--ast-card-border-color",
  cardTitleColor: "--ast-card-title-color",
  actionMarkerBackground: "--ast-action-marker-bg",
  actionMarkerBorderColor: "--ast-action-marker-border-color",
  actionIconColor: "--ast-action-marker-icon-color",
  tagColor: "--ast-tag-color",
  tagBackground: "--ast-tag-bg",
  actionTitleColor: "--ast-action-title-color",
  actionItemColor: "--ast-action-item-color",
  linkColor: "--ast-link-color",
  linkHoverColor: "--ast-link-hover-color",
};

const SIZE_VAR_MAP: Record<keyof AgentStepsTrackerSizes, string> = {
  fontFamily: "--ast-font-family",
  borderRadius: "--ast-radius",
  padding: "--ast-padding",
  titleGap: "--ast-title-gap",
  stepGap: "--ast-step-gap",
  markerSize: "--ast-marker-size",
  markerGap: "--ast-marker-gap",
  actionMarkerSize: "--ast-action-marker-size",
  lineWidth: "--ast-line-width",
  actionLineTrim: "--ast-action-line-trim",
  cardRadius: "--ast-card-radius",
};

function toCssVars(
  values: Record<string, string | undefined> | undefined,
  varMap: Record<string, string>,
): React.CSSProperties {
  const style: Record<string, string> = {};
  if (!values) return style;
  for (const key of Object.keys(values)) {
    const cssVar = varMap[key];
    const value = values[key];
    if (cssVar && value !== undefined) style[cssVar] = value;
  }
  return style as React.CSSProperties;
}

export interface AgentActionItem {
  /** Stable unique identifier for the item. */
  id: string;
  /** The instruction text shown in the checklist. */
  text: string;
  /** When provided, an external-link affordance is shown after the text. */
  href?: string;
  /** Called when the external-link affordance is clicked. */
  onLinkClick?: () => void;
  /** Accessible label for the link. Defaults to "Open link for: {text}". */
  linkAriaLabel?: string;
  /** Override the external-link colour for this item. */
  linkColor?: string;
}

export interface AgentStep {
  /** Stable unique identifier for the step. */
  id: string;
  /** Label shown next to the step marker. */
  label: string;
  /** Icon rendered inside the circular marker. */
  icon?: React.ReactNode;
  /** Visual + behavioural state of the step. Defaults to "completed". */
  status?: AgentStepStatus;
  /** Tag text shown for action-required steps. Defaults to "Action Required". Set to null/"" to hide. */
  tagLabel?: string | null;
  /** Heading shown above the checklist for action-required steps. */
  actionTitle?: string;
  /** Ordered checklist shown for action-required steps. */
  actionItems?: AgentActionItem[];
  /** Per-step colour overrides (cascade to this step's subtree only). */
  colors?: AgentStepsTrackerColors;
}

export interface AgentStepsTrackerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Heading shown above the steps. Set to "" / undefined to hide. */
  title?: string;
  /** Ordered list of steps to display. */
  steps: AgentStep[];
  /** Global colour overrides for the whole component. */
  colors?: AgentStepsTrackerColors;
  /** Global size / layout overrides for the whole component. */
  sizes?: AgentStepsTrackerSizes;
}

function renderActionCard(cls: string, step: AgentStep): React.ReactNode {
  const {
    tagLabel = "Action Required",
    actionTitle,
    actionItems = [],
  } = step;

  return (
    <div className={`${cls}__card`}>
      <div className={`${cls}__card-header`}>
        <div className={`${cls}__card-title-group`}>
          <span
            className={`${cls}__marker ${cls}__marker--action`}
            aria-hidden
          >
            {step.icon}
          </span>
          <span className={`${cls}__card-title`}>{step.label}</span>
        </div>
        {tagLabel && <span className={`${cls}__tag`}>{tagLabel}</span>}
      </div>

      {(actionTitle || actionItems.length > 0) && (
        <div className={`${cls}__card-body`}>
          {actionTitle && (
            <p className={`${cls}__action-title`}>{actionTitle}</p>
          )}
          {actionItems.length > 0 && (
            <ol className={`${cls}__action-list`}>
              {actionItems.map((item) => (
                <li key={item.id} className={`${cls}__action-item`}>
                  <span className={`${cls}__action-text`}>{item.text}</span>
                  {item.href !== undefined && (
                    <a
                      className={`${cls}__action-link`}
                      href={item.href || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        item.linkAriaLabel ?? `Open link for: ${item.text}`
                      }
                      style={
                        item.linkColor
                          ? ({
                              "--ast-link-color": item.linkColor,
                            } as React.CSSProperties)
                          : undefined
                      }
                      onClick={item.onLinkClick}
                    >
                      <ExternalLink size={16} aria-hidden />
                    </a>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

export function AgentStepsTracker({
  title = "Steps Completed by Agent",
  steps,
  colors,
  sizes,
  className,
  style,
  ...rest
}: AgentStepsTrackerProps) {
  const cls = "agent-steps-tracker";
  const rootClass = [cls, className].filter(Boolean).join(" ");

  const rootStyle: React.CSSProperties = {
    ...toCssVars(colors as Record<string, string | undefined>, COLOR_VAR_MAP),
    ...toCssVars(sizes as Record<string, string | undefined>, SIZE_VAR_MAP),
    ...style,
  };

  return (
    <div className={rootClass} style={rootStyle} {...rest}>
      {title && <p className={`${cls}__title`}>{title}</p>}

      <ol className={`${cls}__steps`}>
        {steps.map((step, index) => {
          const status = step.status ?? "completed";
          const isAction = status === "action-required";
          const nextIsAction =
            (steps[index + 1]?.status ?? "completed") === "action-required";
          const stepStyle = toCssVars(
            step.colors as Record<string, string | undefined>,
            COLOR_VAR_MAP,
          );

          const stepClass = [
            `${cls}__step`,
            isAction ? `${cls}__step--action` : `${cls}__step--completed`,
            nextIsAction ? `${cls}__step--before-action` : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <li key={step.id} className={stepClass} style={stepStyle}>
              {isAction ? (
                renderActionCard(cls, step)
              ) : (
                <>
                  <span className={`${cls}__marker`} aria-hidden>
                    {step.icon}
                  </span>
                  <span className={`${cls}__label`}>{step.label}</span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default AgentStepsTracker;
